import type { FinalScore } from '@/game/types';
import { shareCard } from '@/net/api';
import { renderShareCard } from './card';
import {
  challengeMeText,
  challengeMeUrl,
  shareText,
  takeUrl,
  openCode,
  type CardChallenge,
  type CardOutcome,
  type CardUser,
} from './layout';

/**
 * The share flow. Renders both card sizes, gets the link that unfurls (a
 * challenge game's cards go to the server for `/s/:id`; a solo round has no
 * server row and shares the plain origin), then hands the story image to the
 * system share sheet — or, where there is none, back to the caller for the
 * preview dialog. The server is never on the critical path: an upload that
 * fails or dawdles falls back to the plain link. A SETTLED challenge can also be shared from its own screen as
 * "Challenge Me" (`shareChallengeMe`): the card, the QR and the link go to the sharer's profile.
 */

export interface ShareInput {
  final: FinalScore;
  user: CardUser | null;
  /** A challenge game: its code, stake and state, and the server id the cards are stored against. */
  challenge?: (CardChallenge & { challengeId: string }) | undefined;
}

export type ShareOutcome =
  /** The system sheet took it (or the player dismissed it). */
  | { mode: 'native' }
  /** No sheet here: the caller shows the preview with these. */
  | {
      mode: 'preview';
      story: Blob;
      text: string;
      url: string;
      /** True when `url` is a `/s/:id` link that unfurls into the card. */
      unfurls: boolean;
    };

/** The upload's budget before the share goes out with the plain link instead. */
const UPLOAD_TIMEOUT_MS = 4000;
const FILE_NAME = '21wild-score.jpg';
const FILE_MIME = 'image/jpeg';
const SHARE_TITLE = '21 Wild';

async function toBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < buf.length; i += CHUNK) s += String.fromCharCode(...buf.subarray(i, i + CHUNK));
  return btoa(s);
}

/** Stores the cards for a challenge game; null on any failure (the share carries on). */
async function upload(challengeId: string, story: Blob, link: Blob, kind?: 'challenge-me'): Promise<string | null> {
  try {
    const [s, l] = await Promise.all([toBase64(story), toBase64(link)]);
    const res = await Promise.race([
      shareCard(challengeId, { story: s, link: l }, kind),
      new Promise<null>((r) => setTimeout(() => r(null), UPLOAD_TIMEOUT_MS)),
    ]);
    return res?.ok ? res.value.url : null;
  } catch {
    return null;
  }
}

/** Whether this browser can hand an image to a share sheet. */
export function canShareFiles(files: File[]): boolean {
  try {
    return typeof navigator.share === 'function' && navigator.canShare?.({ files }) === true;
  } catch {
    return false;
  }
}

export async function shareResult(input: ShareInput): Promise<ShareOutcome> {
  const challenge: CardChallenge | undefined = input.challenge
    ? {
        code: input.challenge.code,
        entryFee: input.challenge.entryFee,
        status: input.challenge.status,
        outcome: input.challenge.outcome,
      }
    : undefined;
  const base = {
    final: input.final,
    user: input.user,
    // A dare nobody has taken names no one: the server's page and this card agree.
    anonymous: challenge?.status === 'open',
    challenge,
    host: location.host,
    origin: location.origin,
  };
  const [story, link] = await Promise.all([
    renderShareCard({ ...base, size: 'story' }),
    renderShareCard({ ...base, size: 'link' }),
  ]);

  // The plain link: the take link while the challenge is open, the front door otherwise.
  let url = takeUrl(location.origin, openCode(challenge));
  let unfurls = false;
  if (input.challenge) {
    const stored = await upload(input.challenge.challengeId, story, link);
    if (stored) {
      url = stored;
      unfurls = true;
    }
  }
  const text = shareText(input.final.total, location.host, challenge);
  return deliver(story, text, url, unfurls);
}

/** Hands the story image to the system sheet where there is one; otherwise back for the preview. */
async function deliver(story: Blob, text: string, url: string, unfurls: boolean): Promise<ShareOutcome> {
  const files = [new File([story], FILE_NAME, { type: FILE_MIME })];
  if (canShareFiles(files)) {
    try {
      await navigator.share({ files, title: SHARE_TITLE, text, url });
      return { mode: 'native' };
    } catch (err) {
      // Dismissed: nothing more to do. Anything else: the preview still works.
      if (err instanceof Error && err.name === 'AbortError') return { mode: 'native' };
    }
  }
  return { mode: 'preview', story, text, url, unfurls };
}

export interface ChallengeMeInput {
  /** The sharer's own round in the settled challenge, as the card's score and tiles want it. */
  final: FinalScore;
  user: CardUser;
  /** The settled challenge: the cards are stored against it (`/s/:id`). */
  challengeId: string;
  entryFee: number;
  outcome: CardOutcome;
}

/**
 * Shares a SETTLED challenge to invite the next one. The card shows the result and both scores; its QR (and the
 * link) land on the player's public profile at its challenge card — never at an open challenge, which stays
 * anonymous, and the card carries no code. The server keeps the cards so the link unfurls ("<name> won +N
 * $CHAIN on 21 Wild — challenge them"); when that upload fails the share carries the profile link itself.
 */
export async function shareChallengeMe(input: ChallengeMeInput): Promise<ShareOutcome> {
  const base = {
    final: input.final,
    user: input.user,
    anonymous: false,
    // `code: null`: a settled card never prints or encodes one.
    challenge: { code: null, entryFee: input.entryFee, status: 'complete', outcome: input.outcome } satisfies CardChallenge,
    challengeMe: { username: input.user.username },
    host: location.host,
    origin: location.origin,
  };
  const [story, link] = await Promise.all([
    renderShareCard({ ...base, size: 'story' }),
    renderShareCard({ ...base, size: 'link' }),
  ]);
  const stored = await upload(input.challengeId, story, link, 'challenge-me');
  const url = stored ?? challengeMeUrl(location.origin, input.user.username);
  return deliver(story, challengeMeText(input.outcome), url, stored !== null);
}
